import { useState } from 'react';
import type { DeveloperView } from '@dunea/shared';
import { apiCreateDeveloper } from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import { CARD, ErrorBanner, Field, Spinner } from '~/components/workbench/tools/payments/ui';
import {
  apiErrorMessage,
  profileErrors,
  suggestHandle,
  type ProfileDraft,
} from '~/components/marketplace/developer-model';

/** First visit: create the developer profile buyers see on every listing. */
export function Onboarding({ onCreated }: { onCreated: (dev: DeveloperView) => void }) {
  const [draft, setDraft] = useState<ProfileDraft>({
    handle: '',
    displayName: '',
    supportEmail: '',
    website: '',
    bio: '',
  });
  const [handleTouched, setHandleTouched] = useState(false);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errors = profileErrors(draft);
  const shown = tried ? errors : {};

  const set = (patch: Partial<ProfileDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const submit = async () => {
    setTried(true);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const dev = await apiCreateDeveloper({
        handle: draft.handle.trim(),
        displayName: draft.displayName.trim(),
        supportEmail: draft.supportEmail.trim(),
        ...(draft.website.trim() ? { website: draft.website.trim() } : {}),
        ...(draft.bio.trim() ? { bio: draft.bio.trim() } : {}),
      });
      onCreated(dev);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create your developer profile.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className={classNames(CARD, 'max-w-xl space-y-4 p-5')}
    >
      <div>
        <h2 className="text-[15px] font-medium text-primary">Create your developer profile</h2>
        <p className="mt-1 text-[12px] leading-relaxed text-muted">
          Owners see this on every plugin and theme you publish.
        </p>
      </div>
      <Field label="Display name" hint={shown.displayName && <span className="text-danger">{shown.displayName}</span>}>
        <input
          value={draft.displayName}
          onChange={(e) =>
            set({
              displayName: e.target.value,
              ...(handleTouched ? {} : { handle: suggestHandle(e.target.value) }),
            })
          }
          placeholder="Ship Co"
          className="ui-input"
        />
      </Field>
      <Field
        label="Handle"
        hint={
          shown.handle ? (
            <span className="text-danger">{shown.handle}</span>
          ) : (
            'Your public URL name. It cannot be changed later.'
          )
        }
      >
        <input
          value={draft.handle}
          onChange={(e) => {
            setHandleTouched(true);
            set({ handle: e.target.value.toLowerCase() });
          }}
          placeholder="ship-co"
          className="ui-input-mono"
        />
      </Field>
      <Field
        label="Support email"
        hint={shown.supportEmail ? <span className="text-danger">{shown.supportEmail}</span> : 'Shown to buyers.'}
      >
        <input
          type="email"
          value={draft.supportEmail}
          onChange={(e) => set({ supportEmail: e.target.value })}
          className="ui-input"
        />
      </Field>
      <Field label="Website (optional)" hint={shown.website && <span className="text-danger">{shown.website}</span>}>
        <input
          type="url"
          value={draft.website}
          onChange={(e) => set({ website: e.target.value })}
          placeholder="https://"
          className="ui-input"
        />
      </Field>
      <Field label="Bio (optional)" hint={shown.bio && <span className="text-danger">{shown.bio}</span>}>
        <textarea
          value={draft.bio}
          onChange={(e) => set({ bio: e.target.value })}
          rows={3}
          className="ui-input resize-y"
        />
      </Field>
      {error && <ErrorBanner message={error} />}
      <button type="submit" disabled={busy} className="ui-btn-accent">
        {busy && <Spinner />}
        Create profile
      </button>
    </form>
  );
}
